import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Handshake, Plus, Trash2, Power, Users as UsersIcon, ChevronRight, X, Copy, Check } from "lucide-react";

export default function AdminPartnerships() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ code: "", label: "", max_uses: "" });
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [selected, setSelected] = useState(null); // {code, agencies:[]}

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/partnerships");
            setItems(data.items || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e?.preventDefault?.();
        if (!form.code.trim()) return;
        setBusy(true); setFeedback(null);
        try {
            await api.post("/admin/partnerships", {
                code: form.code.trim().toUpperCase(),
                label: form.label.trim() || null,
                max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
            });
            setForm({ code: "", label: "", max_uses: "" });
            setFeedback({ type: "success", msg: "Codice creato" });
            await load();
        } catch (err) {
            setFeedback({ type: "error", msg: err?.response?.data?.detail || "Errore" });
        } finally { setBusy(false); }
    };

    const toggle = async (code) => {
        setBusy(true);
        try {
            await api.post(`/admin/partnerships/${encodeURIComponent(code)}/toggle`);
            await load();
        } finally { setBusy(false); }
    };

    const remove = async (code, hasUses) => {
        const msg = hasUses
            ? `Il codice "${code}" è attivo su alcune agenzie. Eliminandolo, torneranno a Standard. Confermi?`
            : `Eliminare il codice "${code}"?`;
        if (!window.confirm(msg)) return;
        setBusy(true);
        try {
            await api.delete(`/admin/partnerships/${encodeURIComponent(code)}`);
            setSelected(null);
            await load();
        } finally { setBusy(false); }
    };

    const openDrill = async (code) => {
        try {
            const { data } = await api.get(`/admin/partnerships/${encodeURIComponent(code)}/agencies`);
            setSelected(data);
        } catch (err) {
            setFeedback({ type: "error", msg: err?.response?.data?.detail || "Errore drill-down" });
        }
    };

    return (
        <div data-testid="admin-partnerships-tab" className="space-y-8">
            {/* Intro */}
            <div className="flex items-start gap-4 border border-white/10 bg-ink-900/40 p-6">
                <div className="w-12 h-12 flex items-center justify-center border border-gold/40 bg-gold/10 text-gold shrink-0">
                    <Handshake size={22} />
                </div>
                <div>
                    <h2 className="font-serif text-2xl text-ink-50">Gestione Partnership</h2>
                    <p className="text-sm text-ink-400 mt-2 max-w-3xl">
                        Genera codici unici (es. <code className="text-gold">GRUPPO-REMAX</code>, <code className="text-gold">PARTNER-GABETTI</code>) da distribuire ai gruppi partner.
                        Le agenzie che pagano il piano <strong className="text-ink-100">Agenzia Pro €69/mese</strong> possono inserire il codice nel loro Dashboard per sbloccare la pubblicazione manuale illimitata.
                        Il pagamento del piano è comunque obbligatorio: il codice ne potenzia i benefici.
                    </p>
                </div>
            </div>

            {/* Create form */}
            <form onSubmit={create} className="border border-white/10 bg-ink-900/30 p-6 space-y-4">
                <h3 className="font-serif text-lg text-ink-50 flex items-center gap-2"><Plus size={16} className="text-gold" /> Nuovo codice</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                        data-testid="new-partnership-code"
                        value={form.code}
                        onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                        placeholder="CODICE (es. GRUPPO-REMAX)"
                        maxLength={32}
                        className="bg-ink-950/60 border border-white/15 focus:border-gold outline-none px-4 py-2.5 text-sm tracking-wider"
                    />
                    <input
                        data-testid="new-partnership-label"
                        value={form.label}
                        onChange={(e) => setForm({ ...form, label: e.target.value })}
                        placeholder="Etichetta (es. RE/MAX Italia)"
                        maxLength={120}
                        className="bg-ink-950/60 border border-white/15 focus:border-gold outline-none px-4 py-2.5 text-sm"
                    />
                    <input
                        data-testid="new-partnership-max-uses"
                        value={form.max_uses}
                        onChange={(e) => setForm({ ...form, max_uses: e.target.value.replace(/[^0-9]/g, "") })}
                        placeholder="Max uso (opzionale, 0 = illimitato)"
                        className="bg-ink-950/60 border border-white/15 focus:border-gold outline-none px-4 py-2.5 text-sm"
                    />
                </div>
                <button data-testid="create-partnership-btn" type="submit" disabled={busy || !form.code.trim()} className="cta-gold px-5 py-2.5 text-xs disabled:opacity-50">
                    {busy ? "Creazione..." : "Crea Codice"}
                </button>
                {feedback && (
                    <div data-testid={`partnership-feedback-${feedback.type}`} className={`p-3 text-sm border ${feedback.type === "success" ? "border-green-500/40 bg-green-900/20 text-green-200" : "border-red-500/40 bg-red-900/20 text-red-200"}`}>{feedback.msg}</div>
                )}
                <p className="text-[11px] text-ink-400">Formato: lettere maiuscole, numeri e trattini (min 3 caratteri).</p>
            </form>

            {/* List */}
            <div>
                <h3 className="font-serif text-lg text-ink-50 mb-4">Codici attivi</h3>
                {loading ? (
                    <div className="text-sm text-ink-400 border border-white/10 p-6">Caricamento...</div>
                ) : items.length === 0 ? (
                    <div data-testid="no-partnerships" className="border border-white/10 p-10 text-center text-sm text-ink-400">
                        Nessun codice creato. Usa il form sopra per generare il primo.
                    </div>
                ) : (
                    <div className="border border-white/10 divide-y divide-white/5">
                        {items.map((c) => (
                            <PartnershipRow key={c.code} c={c} onToggle={toggle} onDelete={remove} onDrill={openDrill} />
                        ))}
                    </div>
                )}
            </div>

            {selected && <DrillModal data={selected} onClose={() => setSelected(null)} />}
        </div>
    );
}

function PartnershipRow({ c, onToggle, onDelete, onDrill }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try { await navigator.clipboard.writeText(c.code); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_e) { /* noop */ }
    };
    return (
        <div data-testid={`partnership-row-${c.code}`} className="grid grid-cols-12 gap-3 p-4 items-center hover:bg-white/5">
            <div className="col-span-4">
                <div className="flex items-center gap-2">
                    <code className="font-mono text-gold text-sm tracking-wider">{c.code}</code>
                    <button data-testid={`copy-partnership-${c.code}`} onClick={copy} className="text-ink-400 hover:text-gold" title="Copia codice">
                        {copied ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                </div>
                <div className="text-xs text-ink-400 mt-0.5 truncate">{c.label || "—"}</div>
            </div>
            <div className="col-span-2 text-xs">
                <button
                    data-testid={`drill-partnership-${c.code}`}
                    onClick={() => onDrill(c.code)}
                    className="inline-flex items-center gap-1.5 text-ink-100 hover:text-gold border border-white/10 hover:border-gold/40 px-2.5 py-1"
                >
                    <UsersIcon size={12} /> <strong>{c.active_partners || 0}</strong> attivi
                    <ChevronRight size={12} />
                </button>
            </div>
            <div className="col-span-2 text-xs text-ink-400">
                <span>Usi tot: <strong className="text-ink-100">{c.uses_count || 0}</strong></span>
                {c.max_uses ? <span className="block text-[10px]">Max: {c.max_uses}</span> : null}
            </div>
            <div className="col-span-2 text-xs">
                <span className={`inline-flex items-center gap-1.5 px-2 py-1 border ${c.active ? "border-green-500/40 text-green-300 bg-green-900/20" : "border-red-500/40 text-red-300 bg-red-900/20"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${c.active ? "bg-green-400" : "bg-red-400"}`} />
                    {c.active ? "Attivo" : "Disattivo"}
                </span>
            </div>
            <div className="col-span-2 flex items-center justify-end gap-2">
                <button
                    data-testid={`toggle-partnership-${c.code}`}
                    onClick={() => onToggle(c.code)}
                    className="p-2 text-ink-100 hover:text-gold border border-white/10 hover:border-gold/40"
                    title={c.active ? "Disattiva" : "Riattiva"}
                >
                    <Power size={13} />
                </button>
                <button
                    data-testid={`delete-partnership-${c.code}`}
                    onClick={() => onDelete(c.code, (c.all_redeemers || 0) > 0)}
                    className="p-2 text-red-300 hover:text-red-200 border border-red-700/30 hover:border-red-500/60"
                    title="Elimina"
                >
                    <Trash2 size={13} />
                </button>
            </div>
        </div>
    );
}

function DrillModal({ data, onClose }) {
    const { code, agencies = [] } = data;
    return (
        <div data-testid="partnership-drill-modal" className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" onClick={onClose}>
            <div className="max-w-3xl w-full max-h-[80vh] overflow-auto border border-gold/30 bg-ink-950 p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <span className="label-eyebrow text-gold">Drill-down partnership</span>
                        <h3 className="font-serif text-2xl text-ink-50 mt-1">{code?.label || code?.code}</h3>
                        <p className="text-xs text-ink-400 mt-1">Codice: <code className="text-gold">{code?.code}</code> · {agencies.length} agenzie hanno redento questo codice</p>
                    </div>
                    <button data-testid="close-drill-modal" onClick={onClose} className="text-ink-400 hover:text-ink-50"><X size={18} /></button>
                </div>
                {agencies.length === 0 ? (
                    <div className="border border-white/10 p-8 text-center text-sm text-ink-400">Nessuna agenzia ha ancora attivato questo codice.</div>
                ) : (
                    <div className="border border-white/10 divide-y divide-white/5">
                        {agencies.map((a) => (
                            <div key={a.user_id} className="grid grid-cols-12 gap-3 p-4 items-center text-xs">
                                <div className="col-span-4">
                                    <div className="text-ink-50 font-medium">{a.agency_name || a.name}</div>
                                    <div className="text-ink-400 mt-0.5 truncate">{a.email}</div>
                                </div>
                                <div className="col-span-3">
                                    <span className="text-ink-100">Piano: <strong className="text-ink-50">{a.plan_id}</strong></span>
                                    <div className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 border ${a.premium_active ? "border-green-500/40 text-green-300" : "border-red-500/40 text-red-300"}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${a.premium_active ? "bg-green-400" : "bg-red-400"}`} />
                                        {a.premium_active ? "€69 attivo" : "€69 scaduto"}
                                    </div>
                                </div>
                                <div className="col-span-3">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border ${a.partnership_status === "partner" ? "border-gold/40 text-gold" : "border-white/20 text-ink-400"}`}>
                                        {a.partnership_status === "partner" ? "Partner attivo" : "Standard (decaduto)"}
                                    </span>
                                </div>
                                <div className="col-span-2 text-ink-400">
                                    {a.partnership_activated_at ? new Date(a.partnership_activated_at).toLocaleDateString("it-IT") : "—"}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
